import type { Editor, JSONContent } from '@tiptap/core'
import CharacterCount from '@tiptap/extension-character-count'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { TextStyle } from '@tiptap/extension-text-style'
import Typography from '@tiptap/extension-typography'
import Underline from '@tiptap/extension-underline'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useEffect, useRef } from 'react'

const DEFAULT_EDITOR_SURFACE_CLASS =
    'prose prose-lg max-w-none focus:outline-none leading-relaxed text-gray-800 prose-headings:text-gray-900 prose-a:text-blue-600 prose-strong:text-gray-900 prose-em:text-gray-700 min-h-[400px] p-4'

interface TemplateEditorProps {
    /** ProseMirror JSON document (default content source when `initialHtml` is not set). */
    initialValue?: JSONContent
    /** HTML string document — when set, takes precedence over `initialValue` for initial content. */
    initialHtml?: string
    onChange: ({ html, json, content }: { json: JSONContent; html: string; content: string }) => void
    placeholder?: string
    disabled?: boolean
    /** Replaces the default editor surface classes (e.g. modal-friendly height). */
    editorClassName?: string
    /** Wrapper around `EditorContent` (default keeps scroll for full-page editors). */
    wrapperClassName?: string
    /** Optional hard cap for CharacterCount; omit for no limit. */
    characterLimit?: number
    /** Hyperlinks (e.g. stage notes); default true. */
    enableLink?: boolean
    /** Fires once the TipTap instance exists (toolbar, save helpers). */
    onEditorReady?: (editor: Editor) => void
}

const TemplateEditor = ({
    initialValue,
    initialHtml,
    onChange,
    placeholder = 'Start typing...',
    disabled = false,
    editorClassName,
    wrapperClassName,
    characterLimit,
    enableLink = true,
    onEditorReady,
}: TemplateEditorProps) => {
    const editorSurfaceClass = editorClassName ?? DEFAULT_EDITOR_SURFACE_CLASS
    const wrapperClass = wrapperClassName ?? 'h-full overflow-y-auto'
    const lastSyncedInitialHtml = useRef<string | undefined>(undefined)

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4, 5, 6],
                    HTMLAttributes: {
                        class: 'font-bold tracking-tight text-gray-900',
                    },
                },
                bulletList: {
                    HTMLAttributes: {
                        class: 'list-disc pl-6 space-y-2 marker:text-blue-500',
                    },
                },
                orderedList: {
                    HTMLAttributes: {
                        class: 'list-decimal pl-6 space-y-2 marker:text-blue-500 marker:font-medium',
                    },
                },
                blockquote: {
                    HTMLAttributes: {
                        class: 'border-l-4 border-blue-500 pl-6 py-2 italic text-gray-700 bg-blue-50/30 rounded-r-lg my-4',
                    },
                },
                codeBlock: {
                    HTMLAttributes: {
                        class: 'bg-gray-900 text-green-400 rounded-xl p-6 font-mono text-sm overflow-x-auto border border-gray-700 shadow-lg',
                    },
                },
                code: {
                    HTMLAttributes: {
                        class: 'bg-blue-100 text-blue-800 px-2 py-1 rounded-md font-mono text-sm border border-blue-200',
                    },
                },
            }),
            TextStyle,
            Color.configure({
                types: ['textStyle'],
            }),
            Highlight.configure({
                multicolor: true,
            }),
            Typography.configure({
                ellipsis: '…',
                emDash: '—',
                oneHalf: false,
                oneQuarter: false,
                threeQuarters: false,
                plusMinus: false,
                notEqual: false,
                laquo: false,
                raquo: false,
                multiplication: false,
            }),
            CharacterCount.configure(characterLimit !== undefined ? { limit: characterLimit } : {}),
            Placeholder.configure({
                placeholder: placeholder,
                includeChildren: true,
            }),
            Underline,
            ...(enableLink
                ? [
                      Link.configure({
                          openOnClick: false,
                          autolink: true,
                          HTMLAttributes: { rel: 'noopener noreferrer' },
                      }),
                  ]
                : []),
        ],
        content: initialHtml !== undefined ? initialHtml : (initialValue ?? '<p></p>'),
        editable: !disabled,
        onUpdate: ({ editor }) => {
            const json = editor.getJSON()
            const html = editor.getHTML()
            const content = editor.getText()

            onChange({
                json,
                html,
                content: content || '[Empty content]',
            })
        },
        editorProps: {
            attributes: {
                class: editorSurfaceClass,
            },
        },
    })

    useEffect(() => {
        if (editor) {
            onEditorReady?.(editor)
        }
    }, [editor, onEditorReady])

    // Parent-controlled reloads: compare to last synced prop so TipTap-normalized HTML does not loop.
    useEffect(() => {
        if (!editor) return
        if (initialHtml !== undefined) {
            if (lastSyncedInitialHtml.current === initialHtml) return
            lastSyncedInitialHtml.current = initialHtml
            editor.commands.setContent(initialHtml || '<p></p>', { emitUpdate: false })
            return
        }
        lastSyncedInitialHtml.current = undefined
        if (initialValue) {
            editor.commands.setContent(initialValue, { emitUpdate: false })
        }
    }, [editor, initialHtml, initialValue])

    return (
        <div className={wrapperClass}>
            <EditorContent editor={editor} />
        </div>
    )
}

export default TemplateEditor
